import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    Table,
    Input,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    Container,
    Row,
    Col,
    Pagination,
    PaginationItem,
    PaginationLink
} from "reactstrap";
import { toast } from 'react-toastify';
import { FaEye } from 'react-icons/fa';
import CustomTable from "../../components/CustomTable";
import { apiURL } from '../../config';


const FeeView = () => {
    const token = localStorage.getItem("token");

    const [fees, setFees] = useState([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewFee, setViewFee] = useState(null);
    const [filter, setFilter] = useState({
        monthYear: '',
        status: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [currentMonthStatus, setCurrentMonthStatus] = useState('');
    const [userId, setUserId] = useState('');
    const baseURL = apiURL;
    const [loading, setLoading] = useState(false);
    const itemsPerPage = 10;

    useEffect(() => {
        const user = localStorage.getItem("userInfo");
        if (user) {
            const parsedUser = JSON.parse(user);
            setUserId(parsedUser._id);
        }
    }, []);

    useEffect(() => {
        if (userId) {
            fetchFees();
        }
    }, [userId, filter, currentPage]);


    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const fetchFees = async () => {
        setLoading(true);
        try {
            let url = `${baseURL}/student/students/${userId}/fees/filter?page=${currentPage}&limit=${itemsPerPage}`;
            const params = new URLSearchParams();
            if (filter.monthYear) {
                const [year, month] = filter.monthYear.split('-');
                params.append('month', month);
                params.append('year', year);
            }
            if (filter.status) {
                params.append('status', filter.status);
            }

            const paramString = params.toString();
            if (paramString) {
                url += `&${paramString}`
            }
            const response = await fetch(url, {
                method: 'GET', // or any other HTTP method like POST, PUT, DELETE, etc.
                headers: {
                    'Authorization': `Bearer ${token}`, // Add token to headers
                    // You can add other headers here as needed
                },
            });
            const data = await response.json();
            if (data && data.fees && Array.isArray(data.fees)) {
                setFees(data.fees);
                setTotalPages(data.totalPages);
                const currentDate = new Date();
                const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
                const currentYear = currentDate.getFullYear();
                const currentMonthFee = data.fees.find(fee => {
                    const feeDate = formatDate(fee.paidDate);
                    return String(feeDate.getMonth() + 1).padStart(2, '0') === currentMonth && feeDate.getFullYear() === currentYear;
                });
                if (currentMonthFee) {
                    setCurrentMonthStatus(currentMonthFee.status);
                } else {
                    setCurrentMonthStatus('pending');
                }
            } else {
                setFees([]);
                setTotalPages(1);
                setCurrentMonthStatus('pending');
            }
        } catch (error) {
            console.error('Error fetching fees:', error);
            toast.error('Error fetching fees!');
        } finally {
            setLoading(false);
        }
    };

    const handleViewFee = (fee) => {
        setViewFee(fee);
        setViewModalOpen(true);
    };

    const handleFilterChange = (e) => {
        setFilter({ ...filter, [e.target.name]: e.target.value });
    };

    const applyFilters = () => {
        setCurrentPage(1);
        fetchFees();
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const getRowStyle = (status) => {
        switch (status) {
            case 'part paid':
                return 'table-warning';
            case 'pending':
                return 'table-danger';
            case 'paid':
                return 'table-success';
            default:
                return '';
        }
    };

    const columns = [
        "Paid Date",
        "Amount",
        "Full Pay",
        "Status",
        "Notes",
        "Actions"
    ];

    const rows = fees ? fees.map(fee => ({
        paidDate: formatDate(fee.paidDate),
        amount: fee.amount,
        isFullPay: fee.isFullPay ? 'Yes' : 'No',
        status: fee.status,
        notes: fee.notes,
        actions: (
            <>
                <Button
                    color="info"
                    size="sm"
                    onClick={() => handleViewFee(fee)}
                >
                    <FaEye />
                </Button>
            </>
        ),
    })) : [];

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === currentPage}>
                    <PaginationLink onClick={() => handlePageChange(i)}>
                        {i}
                    </PaginationLink>
                </PaginationItem>
            );
        }
        return (
            <Pagination>
                {paginationItems}
            </Pagination>
        );
    };

    return (
        <>
            <div
                className="header   d-flex align-items-center"
                style={{
                    minHeight: "450px",
                    backgroundImage:
                        "url(" + require("../../assets/img/theme/alRehman.jpg") + ")",
                    backgroundSize: "cover",
                    backgroundPosition: "center top",
                }}
            >
                <span className="mask bg-gradient-default opacity-8" />
                <Container className="d-flex align-items-center" fluid>
                    <Row>
                        <Col lg="7" md="10">
                            <h1 className="display-4 text-white">Fee Management</h1>
                            <p className="text-white mt-0 mb-5">
                                You can see your fee record here.
                            </p>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Container className="mt-5">
                <Card>
                    <CardHeader className="d-flex justify-content-between align-items-center">
                        <h3 className="mb-0">Fee Information</h3>
                    </CardHeader>
                    <CardBody>
                        {currentMonthStatus && (
                            <div style={{ fontSize: '1.2rem', marginBottom: '15px' }}>
                                Current Month Status: <span style={{ fontWeight: 'bold' }} className={getRowStyle(currentMonthStatus)}>{currentMonthStatus.toUpperCase()}</span>
                            </div>
                        )}
                        <div className="d-flex align-items-center mb-3 flex-wrap" style={{ gap: '8px' }}>
                            <Input
                                bsSize="sm"
                                type="select"
                                name="monthYear"
                                value={filter.monthYear}
                                onChange={handleFilterChange}
                                style={{ width: '200px' }}
                            >
                                <option value="">Select Month and Year</option>
                                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                                    Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                                        <option key={`${year}-${month < 10 ? `0${month}` : month}`} value={`${year}-${month < 10 ? `0${month}` : month}`}>
                                            {`${month < 10 ? `0${month}` : month}-${year}`}
                                        </option>
                                    ))
                                )).flat()}
                            </Input>

                            <Button color="primary" size="sm" onClick={applyFilters}>
                                Apply Filters
                            </Button>
                        </div>
                        {loading ? (
                            <div className="text-center">
                                <Spinner color="primary" />
                            </div>
                        ) : (
                            <CustomTable columns={columns} rows={rows} />
                        )}
                        {totalPages > 1 && renderPagination()}
                    </CardBody>
                </Card>
                <Modal isOpen={viewModalOpen} toggle={() => setViewModalOpen(false)}>
                    <ModalHeader toggle={() => setViewModalOpen(false)}>Fee Details</ModalHeader>
                    <ModalBody>
                        {viewFee && (
                            <div>
                                <p><strong>Paid Date:</strong> {formatDate(viewFee.paidDate)}</p>
                                <p><strong>Amount:</strong> {viewFee.amount}</p>
                                <p><strong>Full Pay:</strong> {viewFee.isFullPay ? 'Yes' : 'No'}</p>
                                <p><strong>Status:</strong> {viewFee.status}</p>
                                <p><strong>Notes:</strong> {viewFee.notes}</p>
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setViewModalOpen(false)}>
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </>
    );
};

export default FeeView;
